import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { NotificationService } from '../../services/notification.service';
import { finalize, Observable, tap } from 'rxjs';
import {
    ClearTableNotifications,
    LoadNotifications,
    LoadTableNotifications,
    LoadUserNotification,
    MarkUserNotificationAsRead
} from './notification.actions';
import { INotification } from '../../interfaces/INotification';
import { IUserNotification } from '../../interfaces/IUserNotification';
import { append, insertItem, patch, updateItem } from '@ngxs/store/operators';

export interface NotificationStateModel {
    notification: INotification;
    areNotificationsLoading: boolean;
    areNotificationsCursorLoading: boolean;
    tableNotification: INotification;
    areTableNotificationsLoading: boolean;
    areTableNotificationsCursorLoading: boolean;
}

@State<NotificationStateModel>({
    name: 'notificationState',
    defaults: {
        notification: {
            cursor: null,
            userNotifications: []
        },
        areNotificationsLoading: false,
        areNotificationsCursorLoading: false,
        tableNotification: {
            cursor: null,
            userNotifications: []
        },
        areTableNotificationsLoading: false,
        areTableNotificationsCursorLoading: false
    }
})
@Injectable()
export class NotificationState {
    private notificationService = inject(NotificationService);

    @Selector()
    public static notifications(state: NotificationStateModel): INotification {
        return state.notification;
    }

    @Selector()
    public static areNotificationsLoading(state: NotificationStateModel): boolean {
        return state.areNotificationsLoading;
    }

    @Selector()
    public static areNotificationsCursorLoading(state: NotificationStateModel): boolean {
        return state.areNotificationsCursorLoading;
    }

    @Selector()
    public static tableNotifications(state: NotificationStateModel): INotification {
        return state.tableNotification;
    }

    @Selector()
    public static areTableNotificationsLoading(state: NotificationStateModel): boolean {
        return state.areTableNotificationsLoading;
    }

    @Selector()
    public static areTableNotificationsCursorLoading(state: NotificationStateModel): boolean {
        return state.areTableNotificationsCursorLoading;
    }

    @Action(LoadNotifications)
    public loadNotifications(
        ctx: StateContext<NotificationStateModel>,
        { payload }: LoadNotifications
    ): Observable<INotification> {
        if (payload?.cursor) {
            ctx.patchState({ areNotificationsCursorLoading: true });
        } else {
            ctx.patchState({ areNotificationsLoading: true });
        }
        return this.notificationService.getNotifications(payload?.cursor).pipe(
            tap((notificationResult) => {
                ctx.setState(
                    patch({
                        notification: patch({
                            ...notificationResult,
                            userNotifications: append(notificationResult.userNotifications)
                        })
                    })
                );
            }),
            finalize(() => ctx.patchState({ areNotificationsLoading: false, areNotificationsCursorLoading: false }))
        );
    }

    @Action(ClearTableNotifications)
    public clearTableNotifications(ctx: StateContext<NotificationStateModel>): void {
        ctx.setState(
            patch({
                tableNotification: patch({
                    cursor: null,
                    userNotifications: []
                })
            })
        );
    }

    @Action(LoadTableNotifications)
    public loadTableNotifications(
        ctx: StateContext<NotificationStateModel>,
        { payload }: LoadTableNotifications
    ): Observable<INotification> {
        if (payload?.cursor) {
            ctx.patchState({ areTableNotificationsCursorLoading: true });
        } else {
            ctx.patchState({ areTableNotificationsLoading: true });
        }
        return this.notificationService.getNotifications(payload?.cursor).pipe(
            tap((tableNotificationResult) => {
                ctx.setState(
                    patch({
                        tableNotification: patch({
                            ...tableNotificationResult,
                            userNotifications: append(tableNotificationResult.userNotifications)
                        })
                    })
                );
            }),
            finalize(() =>
                ctx.patchState({ areTableNotificationsLoading: false, areTableNotificationsCursorLoading: false })
            )
        );
    }

    @Action(LoadUserNotification)
    public loadNotification(
        ctx: StateContext<NotificationStateModel>,
        { payload }: LoadUserNotification
    ): Observable<IUserNotification> {
        return this.notificationService.getUserNotification(payload.id).pipe(
            tap((userNotification) => {
                ctx.setState(
                    patch({
                        notification: patch({
                            userNotifications: insertItem(userNotification, 0)
                        })
                    })
                );
            })
        );
    }

    @Action(MarkUserNotificationAsRead)
    public markUserNotificationAsRead(
        ctx: StateContext<NotificationStateModel>,
        { payload }: MarkUserNotificationAsRead
    ): Observable<IUserNotification> {
        return this.notificationService.markUserNotificationAsRead(payload.id).pipe(
            tap(() => {
                ctx.setState(
                    patch({
                        notification: patch({
                            userNotifications: updateItem<IUserNotification>(
                                (userNotification) => userNotification.id === payload.id,
                                patch({ read: true })
                            )
                        })
                    })
                );
            })
        );
    }
}
